import React, { useCallback } from "react";
import {
  Route,
  Routes,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Box, VStack } from "@chakra-ui/layout";
import { Image } from "@chakra-ui/react";

import { useUserContext } from "./context/UserContext";
import { Login } from "./pages/Login/Login";

import { Layout } from "./components/Layout/Layout";
import { CurrentUser } from "./components/CurrentUser/CurrentUser";
import { DataApiMenu } from "./components/Menu/DataApiMenu";
import { AppTitle } from "./components/AppTitle/AppTitle";
import { ErrorHandler } from "./components/ErrorBoundary/ErrorBoundary";
import { HStack } from "@chakra-ui/react";
import { LoadingIndicator } from "./components/LoadingIndicator/LoadingIndicator";
import { ErrorMessage } from "./components/ErrorMessage/ErrorMessage";

import cherryLogo from "assets/cherryhub_logo.svg";
import { useLayoutContext } from "./context/LayoutContext";
import { Environment } from "./pages/Environments/Environment";
import { OutboundIpDetails } from "./pages/Environments/OutboundIpDetails";
import { Configurations } from "./pages/Configurations/Configurations";
import { Apsc } from "./pages/APSC/Apsc";
import { TestCertificate } from "./pages/APSC/TestCertificate";
import { Dashboard } from "./pages/Dashboard/Dashboard";
import { HomeMenu } from "./components/Menu/HomeMenu";
import { AdminMenu } from "./components/Menu/AdminMenu";
import { Businesses } from "./pages/Business/Businesses";
import { ModelContextProvider } from "./context/ModelContext";
import { Venues } from "./pages/Venues/Venues";
import { BusinessDetail } from "./pages/Business/BusinessDetail";
import { Integrations } from "./pages/Integration/Integrations";
import { IntegrationDetail } from "./pages/Integration/IntegrationDetail";
import { IntegrationConfiguration } from "./pages/Integration/IntegrationConfiguration";
import { IntegrationDataStore } from "./pages/Integration/IntegrationDataStore";
import { Redemptions } from "./pages/Redemption/Redemptions";
import { RedemptionDetail } from "./pages/Redemption/RedemptionDetail";
import { RegistrationFormConfigurations } from "./pages/RegistrationFormConfiguration/RegistrationFormConfigurations";
import { RegistrationFormConfigurationDetail } from "./pages/RegistrationFormConfiguration/RegistrationFormConfigurationDetail";
import { MembershipRenewals } from "./pages/MembershipRenewal/MembershipRenewals";
import { MembershipRenewalDetail } from "./pages/MembershipRenewal/MembershipRenewalDetail";
import { VenueDetail } from "./pages/Venues/VenueDetail";
import { Reports } from "./pages/Reports/Reports";
import { StandardReport } from "./pages/Reports/StandardReport";
import { ComparativeReport } from "./pages/Reports/ComparativeReport";
import { IntegrationMembershipConfiguration } from "./pages/Integration/IntegrationMembershipConfiguration";
import { IntegrationLoyaltyConfiguration } from "./pages/Integration/IntegrationLoyaltyConfiguration";
import { IntegrationAccessRights } from "./pages/Integration/IntegrationAccessRights";
import { IntegrationSecrets } from "./pages/Integration/IntegrationSecrets";
import { Applications } from "./pages/Applications/Applications";
import { ApplicationDetail } from "./pages/Applications/ApplicationDetail";
import { Users } from "./pages/Users/Users";
import { UserDetail } from "./pages/Users/UserDetail";
import { Members } from "./pages/Members/Members";
import { MemberDetail } from "./pages/Members/MemberDetail";
import { CherryPayCards } from "./pages/CherryPayCards/CherryPayCards";
import { CherryPayCardDetail } from "./pages/CherryPayCards/CherryPayCardDetail";
import { CherryPayCardTransactions } from "./pages/CherryPayCards/CherryPayCardTransactions";
import { StatusReport } from "./pages/Reports/StatusReport";
import { DMCStatusReport } from "./pages/Reports/DMCStatusReport";
import { EMLConfigurationDetail } from "./pages/EMLConfiguration/EMLConfigurationDetail";
import { EventsModuleConfigurationDetail } from "./pages/EventsModuleConfiguration/EventsModuleConfigurationDetail";
import { PassEditor } from "./pages/Business/PassEditor";
import { PassConfigurations } from "./pages/PassConfiguration/PassConfigurations";
import { TwilioConfiguration } from "./pages/TwilioConfiguration/TwilioConfiguration";
import { LandingPageConfigurations } from "./pages/LandingPageConfiguration/LandingPageConfigurations";
import { LandingPageConfigurationDetail } from "./pages/LandingPageConfiguration/LandingPageConfigurationDetail";
import { MemberSelfServiceConfigurations } from "./pages/MemberSelfServiceConfiguration/MemberSelfServiceConfigurations";
import { MemberSelfServiceConfigurationDetail } from "./pages/MemberSelfServiceConfiguration/MemberSelfServiceConfigurationDetail";
import { TwilioBillingUsagesReport } from "./pages/Reports/TwilioBillingUsagesReport";
import { PushNotificationsConfigurationDetail } from "./pages/PushNotificationsConfiguration/PushNotificationsConfigurationDetail";
import { GeoFenceConfigurationDetail } from "./pages/GeoFenceConfiguration/GeoFenceConfigurationDetail";
import { GeoLocationDetail } from "./pages/GeoFenceConfiguration/GeoLocationDetail";
import { GeoLocationCreate } from "./pages/GeoFenceConfiguration/GeoLocationCreate";
import { GeoLocationGroupDetail } from "./pages/GeoFenceConfiguration/GeoLocationGroupDetail";
import { GeoLocationGroupCreate } from "./pages/GeoFenceConfiguration/GeoLocationGroupCreate";
import { ExtendedFields } from "./pages/ExtendedFields/ExtendedFields";
import { ExtendedFieldDetail } from "./pages/ExtendedFields/ExtendedFieldDetail";

const getCurrentMenu = (pathName: string) => {
  const paths = pathName.split("/");
  const firstPath = (paths.length > 1 && paths[1].toLowerCase()) || "";
  switch (firstPath) {
    case "data-api":
      return DataApiMenu;
    case "admin":
      return AdminMenu;
    case "":
    default:
      return HomeMenu;
  }
};

const AppLayout = () => {
  const { user } = useUserContext();
  const location = useLocation();
  const { isDesktop } = useLayoutContext();
  const AppMenu = getCurrentMenu(location.pathname);

  return (
    <Layout location={location.pathname}>
      <Layout.Title>
        <HStack>
          <AppTitle />
          {isDesktop && (
            <Box>
              <CurrentUser />
            </Box>
          )}
        </HStack>
      </Layout.Title>
      <Layout.Menu>
        {!isDesktop && (
          <VStack w="100%" bg="cherryUi.600" padding="2" marginBottom="2">
            <Image src={cherryLogo} boxSize="100px" title="Chery hub" />
            <CurrentUser />
          </VStack>
        )}
        {!!user && (
          <VStack spacing="2" w="100%" className="test">
            <AppMenu isDesktop={isDesktop} />
          </VStack>
        )}
      </Layout.Menu>
      <Layout.Content>
        <ErrorHandler
          renderErrorState={() => (
            <ErrorMessage>There was an error loading the page.</ErrorMessage>
          )}
        >
          <ModelContextProvider>
            <Outlet />
          </ModelContextProvider>
        </ErrorHandler>
      </Layout.Content>
    </Layout>
  );
};

const RequireAuth = ({ children }: { children: JSX.Element }) => {
  const { user, isLoading } = useUserContext();

  if (isLoading) {
    return <LoadingIndicator />;
  } else if (!user) {
    return <Login />;
  }

  return children;
};

const LoginRoute = () => {
  const navigate = useNavigate();
  const onLoginSuccess = useCallback(() => {
    navigate("/data-api/sysadmin/environment");
  }, [navigate]);
  return <Login onLoginSuccess={onLoginSuccess} />;
};

export const AppRoutes = () => (
  <Routes>
    <Route
      element={
        <RequireAuth>
          <AppLayout />
        </RequireAuth>
      }
    >
      <Route path="/" element={<Dashboard />} />

      <Route path="/data-api/sysadmin/environment" element={<Environment />} />
      <Route
        path="/data-api/sysadmin/environment/outbound-ip-details"
        element={<OutboundIpDetails />}
      />
      <Route
        path="/data-api/sysadmin/configuration"
        element={<Configurations />}
      />
      <Route
        path="/data-api/sysadmin/apple-pass-signing-certificates"
        element={<Apsc />}
      />
      <Route
        path="/data-api/sysadmin/apple-pass-signing-certificates/test-pkcs12-certificate"
        element={<TestCertificate />}
      />

      <Route
        path="/data-api/sysadmin/twilio-configuration/new"
        element={<TwilioConfiguration create={true} />}
      />
      <Route
        path="/data-api/sysadmin/twilio-configuration/:twilioConfigurationId"
        element={<TwilioConfiguration />}
      />

      <Route path="/admin/businesses" element={<Businesses />} />
      <Route
        path="/admin/businesses/:businessId"
        element={<BusinessDetail />}
      />

      <Route
        path="/admin/businesses/:businessId/pass-configurations/:passConfigurationId"
        element={<PassEditor />}
      />

      <Route path="/admin/businesses/:businessId/venues" element={<Venues />} />

      <Route
        path="/admin/businesses/:businessId/venues/:venueId"
        element={<VenueDetail />}
      />

      <Route
        path="/admin/businesses/:businessId/venues/new"
        element={<VenueDetail create={true} />}
      />

      <Route
        path="/admin/businesses/:businessId/redemptions"
        element={<Redemptions />}
      />
      <Route
        path="/admin/businesses/:businessId/redemptions/:redemptionId"
        element={<RedemptionDetail />}
      />
      <Route
        path="/admin/businesses/:businessId/redemptions/new"
        element={<RedemptionDetail create={true} />}
      />

      <Route
        path="/admin/businesses/:businessId/integrations"
        element={<Integrations />}
      />
      <Route
        path="/admin/businesses/:businessId/integrations/new"
        element={<IntegrationDetail create={true} />}
      />
      <Route
        path="/admin/businesses/:businessId/integrations/:integrationId"
        element={<IntegrationDetail />}
      />
      <Route
        path="/admin/businesses/:businessId/integrations/:integrationId/configuration"
        element={<IntegrationConfiguration />}
      />
      <Route
        path="/admin/businesses/:businessId/integrations/:integrationId/configuration/:configurationTypeName"
        element={<IntegrationConfiguration />}
      />
      <Route
        path="/admin/businesses/:businessId/integrations/:integrationId/data-store"
        element={<IntegrationDataStore />}
      />
      <Route
        path="/admin/businesses/:businessId/integrations/:integrationId/membership-management-configuration"
        element={<IntegrationMembershipConfiguration />}
      />
      <Route
        path="/admin/businesses/:businessId/integrations/:integrationId/membership-loyalty-configuration"
        element={<IntegrationLoyaltyConfiguration />}
      />
      <Route
        path="/admin/businesses/:businessId/integrations/:integrationId/permissions"
        element={<IntegrationAccessRights />}
      />
      <Route
        path="/admin/businesses/:businessId/integrations/:integrationId/integration-secrets"
        element={<IntegrationSecrets />}
      />

      <Route
        path="/admin/businesses/:businessId/registration-form-configurations"
        element={<RegistrationFormConfigurations />}
      />
      <Route
        path="/admin/businesses/:businessId/registration-form-configurations/new"
        element={<RegistrationFormConfigurationDetail create={true} />}
      />
      <Route
        path="/admin/businesses/:businessId/registration-form-configurations/:registrationId"
        element={<RegistrationFormConfigurationDetail />}
      />

      <Route
        path="/admin/businesses/:businessId/extended-fields"
        element={<ExtendedFields />}
      />
      <Route
        path="/admin/businesses/:businessId/extended-fields/new"
        element={<ExtendedFieldDetail create={true} />}
      />
      <Route
        path="/admin/businesses/:businessId/extended-fields/:fieldId"
        element={<ExtendedFieldDetail />}
      />

      <Route
        path="/admin/businesses/:businessId/membership-renewals"
        element={<MembershipRenewals />}
      />
      <Route
        path="/admin/businesses/:businessId/membership-renewals/:membershipRenewalId"
        element={<MembershipRenewalDetail />}
      />
      <Route
        path="/admin/businesses/:businessId/membership-renewals/new"
        element={<MembershipRenewalDetail create={true} />}
      />

      <Route
        path="/admin/businesses/:businessId/landing-page-configurations"
        element={<LandingPageConfigurations />}
      />
      <Route
        path="/admin/businesses/:businessId/landing-page-configurations/:landingPageConfigurationId"
        element={<LandingPageConfigurationDetail />}
      />
      <Route
        path="/admin/businesses/:businessId/landing-page-configurations/new"
        element={<LandingPageConfigurationDetail create={true} />}
      />

      <Route
        path="/admin/businesses/:businessId/member-self-service-configurations"
        element={<MemberSelfServiceConfigurations />}
      />
      <Route
        path="/admin/businesses/:businessId/member-self-service-configurations/new"
        element={<MemberSelfServiceConfigurationDetail create={true} />}
      />
      <Route
        path="/admin/businesses/:businessId/member-self-service-configurations/:memberSelfServiceConfigurationId"
        element={<MemberSelfServiceConfigurationDetail />}
      />

      <Route
        path="/admin/businesses/:businessId/eml-configuration"
        element={<EMLConfigurationDetail />}
      />
      <Route
        path="/admin/businesses/:businessId/eml-configuration/new"
        element={<EMLConfigurationDetail create={true} />}
      />
      <Route
        path="/admin/businesses/:businessId/events-module-configuration"
        element={<EventsModuleConfigurationDetail />}
      />
      <Route
        path="/admin/businesses/:businessId/push-notifications-configuration"
        element={<PushNotificationsConfigurationDetail />}
      />
      <Route
        path="/admin/businesses/:businessId/push-notifications-configuration/new"
        element={<PushNotificationsConfigurationDetail create={true} />}
      />
      <Route
        path="/admin/businesses/:businessId/geo-fence-configuration"
        element={<GeoFenceConfigurationDetail />}
      />
      <Route
        path="/admin/businesses/:businessId/geo-fence-configuration/geo-locations/new"
        element={<GeoLocationCreate />}
      />
      <Route
        path="/admin/businesses/:businessId/geo-fence-configuration/geo-locations/:geoLocationId"
        element={<GeoLocationDetail />}
      />
      <Route
        path="/admin/businesses/:businessId/geo-fence-configuration/geo-location-groups/new"
        element={<GeoLocationGroupCreate />}
      />
      <Route
        path="/admin/businesses/:businessId/geo-fence-configuration/geo-location-groups/:geoLocationGroupId"
        element={<GeoLocationGroupDetail />}
      />

      <Route
        path="/admin/businesses/:businessId/pass-configurations"
        element={<PassConfigurations />}
      />

      <Route path="/admin/reports" element={<Reports />} />
      <Route path="/admin/reports/standard" element={<StandardReport />} />
      <Route path="/admin/reports/status" element={<StatusReport />} />
      <Route
        path="/admin/reports/comparative"
        element={<ComparativeReport />}
      />
      <Route path="/admin/reports/dmc-status" element={<DMCStatusReport />} />
      <Route
        path="/admin/reports/twilio-billing"
        element={<TwilioBillingUsagesReport />}
      />

      <Route path="/admin/applications" element={<Applications />} />
      <Route
        path="/admin/applications/new"
        element={<ApplicationDetail create={true} />}
      />
      <Route
        path="/admin/applications/:applicationId"
        element={<ApplicationDetail />}
      />

      <Route path="/admin/users" element={<Users />} />
      <Route path="/admin/users/new" element={<UserDetail create={true} />} />
      <Route path="/admin/users/:uid" element={<UserDetail />} />

      <Route path="/admin/members" element={<Members />} />
      <Route path="/admin/members/:memberId" element={<MemberDetail />} />

      <Route path="/admin/cherrypay-cards" element={<CherryPayCards />} />
      <Route
        path="/admin/cherrypay-cards/:cardId"
        element={<CherryPayCardDetail />}
      />
      <Route
        path="/admin/cherrypay-cards/:cardId/transactions"
        element={<CherryPayCardTransactions />}
      />
    </Route>
    <Route path="/login" element={<LoginRoute />} />
  </Routes>
);
